import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ASSETS_WITH_COUNT,
  SALE_UPDATE_ASSETS,
  SALE_ASSET_LOT_NUMBERS,
} from './queries';
import { useQuery, useMutation } from '@apollo/client';
import {
  ModalTableContainer,
  ContentContainer,
} from '../../elements';
import SubNav from './SubNav';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text } from 'react-native';
import moment from 'moment';
import styled from 'styled-components/native';

import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import { Button } from 'app/src/elements/buttons';
import AuthContext from 'app/src/contexts/AuthContext';
import { SALE } from 'app/src/components/Sales/queries';

import {
  LOT_NUMBER_YEARS,
  lotSuffixesMap,
  lotSuffixOptions,
} from 'app/src/components/Sales/Process';

const LotNumberContainer = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  max-width: 220px;
  white-space: nowrap;
`;

const AddableAssets = props => {
  const { id } = useParams();
  const { lotPrefixes } = useContext(AuthContext);
  const { withNav } = props;
  const [selected, setSelected] = useState([]);
  const [refetch, setRefetch] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const [formError, setFormError] = useState('');

  const queryVariables = {
    saleId: id,
    filterColumns: [],
    filterNames: [],
    scopes: ['salable']
  };

  const { data: saleAssetData } = useQuery(SALE_ASSET_LOT_NUMBERS, {
    variables: { saleId: id }
  });

  const { data: saleData } = useQuery(SALE, {
    variables: { id }
  });

  const isPublicAuction = !!saleData?.sale?.publicAuction;

  const [updateAssets] = useMutation(SALE_UPDATE_ASSETS, {
    onCompleted: data => {
      const errors = data?.saleUpdateAssets?.errors || [];

      if (errors.length > 0) {
        const message = errors.map(error => error.message).join(', ');
        setFormError('Unable to add asset(s). Please fix the highlighted run number field.');
        setSelected(current => current.map(item => ({
          ...item,
          error: message
        })));
        return;
      }

      window.location.replace(`/sales/${id}`);
    },
    onError: error => {
      setFormError('Unable to add asset(s). Please fix the highlighted run number field.');
      setSelected(current => current.map(item => {
        if (!item.lotNumberSuffix) { return item; }
        return { ...item, error: error.message };
      }));
    }
  });

  const lotSuffixesAvailable = useMemo(
    () => lotSuffixesMap(lotPrefixes || []),
    [lotPrefixes]
  );

  const setFilter = (key, value) => {
    const currentFilters = { ...filters };
    currentFilters[key] = value;
    setFilters(currentFilters);
  };

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  let currentYear = moment().format('YYYY');
  let years = [];
  for (let x = 0; x < 100; x++) {
    years.push(currentYear - x);
  }

  const findSelected = idToFind => selected.find(add => add.id === idToFind);

  const columns = [
    {
      key: 'selected',
      label: '',
      sort: 'prevent',
      content: ({ entry }) => (
        <input
          type='checkbox'
          checked={!!selected.find(asset => asset.id === entry.id)}
          onChange={e => handleChange(e.target.checked, entry)}
        />
      )
    },
    {
      key: 'lotNumber',
      label: isPublicAuction ? 'Run Number' : 'Lot Number',
      sort: 'prevent',
      width: isPublicAuction ? 190 : 110,
      content: ({ entry }) => {
        const item = findSelected(entry.id);

        if (!item) {
          return <Text></Text>;
        }

        const suffixOptions = isPublicAuction ? [] : lotSuffixOptions(
          lotSuffixesAvailable,
          item.lotNumberPrefix,
          item.lotNumberYear,
          (saleAssetData?.saleAssets || []).concat(selected),
        );

        const errorStyle = item.error ? {
          border: '1px solid #c62828'
        } : {};

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <LotNumberContainer>
              <select
                value={item.lotNumberPrefix || ''}
                onChange={e => handleLotNumberChange(entry, { prefix: e.target.value })}
                style={item.prefixError ? errorStyle : {}}
              >
                <option></option>
                {lotPrefixes.map(prefix => (
                  <option key={prefix.code} value={prefix.code}>
                    {prefix.code}
                  </option>
                ))}
              </select>

              {item.lotNumberPrefix && (
                <>
                  <span>-</span>

                  {isPublicAuction ? (
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={item.lotNumberSuffix || ''}
                      onChange={e => handleLotNumberChange(entry, { suffix: e.target.value })}
                      style={{
                        width: '80px',
                        ...(item.error ? errorStyle : {})
                      }}
                    />
                  ) : (
                    <>
                      <select
                        value={item.lotNumberYear || ''}
                        onChange={e => handleLotNumberChange(entry, { year: e.target.value })}
                      >
                        <option></option>
                        {LOT_NUMBER_YEARS.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>

                      <select
                        value={item.lotNumberSuffix || ''}
                        onChange={e => handleLotNumberChange(entry, { suffix: e.target.value })}
                      >
                        <option></option>
                        {item.lotNumberSuffix && (
                          <option
                            key="selected"
                            value={item.lotNumberSuffix}
                          >
                            {item.lotNumberSuffix}
                          </option>
                        )}
                        {suffixOptions.map(suffix => (
                          <option key={suffix} value={suffix}>{suffix}</option>
                        ))}
                      </select>
                    </>
                  )}
                </>
              )}
            </LotNumberContainer>

            {item.error ? (
              <div style={{
                color: '#c62828',
                fontSize: '12px',
                fontWeight: 600,
                marginTop: '4px'
              }}>
                {item.error}
              </div>
            ) : null}
          </div>
        );
      }
    },
    {
      key: 'vin',
      label: 'VIN',
      content: (entry) => {
        return (
          <View>
            <Link to={`/sellers/${entry.entry.id}/assets/${entry.entry.id}`}>
              {entry.entry.vin}
            </Link>
          </View>
        );
      }
    },
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'make',
      label: 'Make',
    },
    {
      key: 'model',
      label: 'Model',
    },
    {
      key: 'seller',
      label: 'Seller',
      value: ({ value }) => value.name,
    },
    {
      key: 'clientNumber',
      label: 'Client #',
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: (entry) => {
        return (
          <View style={{ display: 'inline-flex' }}>
            <Link
              to={`/sellers/${entry.entry.seller?.id}/assets/${entry.entry.id}`}
              style={{ marginLeft: '10px' }}
            >
              View/Edit
            </Link>
          </View>
        );
      }
    }
  ];

  const buttons = (
    <View style={{ display: 'flex', flexDirection: 'row' }}>
      <Filter
        clearFilters={() => {
          setFilters({});
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{ padding: '20px' }}>
          <Text>Seller Name:</Text>
          <input
            type="text"
            name="seller"
            onChange={e => setFilter('seller', e.target.value)}
            value={filters.seller || ''}
          />
          <Text>VIN:</Text>
          <input
            type={'text'}
            name="vin"
            onChange={e => setFilter('vin', e.target.value)}
            value={filters.vin || ''}
          />
          <Text>Year:</Text>
          <select
            type={'select'}
            name="year"
            onChange={e => setFilter('year', e.target.value)}
            value={filters.year || ''}
          >
            <option key={`year-any`}>{'Any'}</option>
            {years.map(year => <option key={`year-${year}`}>{year}</option>)}
          </select>
          <Text>Make:</Text>
          <input
            type={'text'}
            name="make"
            onChange={e => setFilter('make', e.target.value)}
            value={filters.make || ''}
          />
          <Text>Model:</Text>
          <input
            type={'text'}
            name="model"
            onChange={e => setFilter('model', e.target.value)}
            value={filters.model || ''}
          />
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <ModalTableContainer>
      {withNav && <SubNav refetch={refetch} />}

      <ContentContainer>
        <Text><b>All Assets</b></Text>

        {formError ? (
          <View style={{ marginBottom: '10px' }}>
            <Text style={{ color: 'red' }}>{formError}</Text>
          </View>
        ) : null}

        <DataTable.Gql
          setRefetch={setRefetch}
          query={ASSETS_WITH_COUNT}
          queryName='assetsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={[10, 25, 50, 'All']}
          setSettings={setSettings}
          dataUpdated={data => data?.assetsWithCount}
          htmlTable
        />

        <Button
          label="Add to Sale"
          onPress={() => handleSubmit()}
        />
      </ContentContainer>
    </ModalTableContainer>
  );

  function handleChange(checked, asset) {
    setFormError('');

    let newSelected = [...selected];
    let present = newSelected.find(add => add.id === asset.id);

    if (!checked && present) {
      newSelected = selected.filter(add => add.id !== asset.id);
    } else if (checked && !present) {
      newSelected.push({
        id: asset.id,
        lotNumberPrefix: '',
        lotNumberYear: '',
        lotNumberSuffix: '',
        manualRunOrder: '',
        error: '',
      });
    }

    setSelected(newSelected);
  }

  function handleLotNumberChange(asset, { prefix, year, suffix }) {
    const item = selected.find(add => add.id === asset.id);
    if (!item) { return; }

    setFormError('');
    item.error = '';

    if (typeof prefix !== 'undefined') {
      item.lotNumberPrefix = prefix;

      if (isPublicAuction) {
        item.lotNumberYear = null;
      } else {
        item.lotNumberYear = null;
        item.lotNumberSuffix = null;
      }
    }

    if (!isPublicAuction && typeof year !== 'undefined') {
      item.lotNumberYear = year;
      item.lotNumberSuffix = null;
    }

    if (typeof suffix !== 'undefined') {
      item.lotNumberSuffix = suffix;
      item.manualRunOrder = isPublicAuction ? suffix : null;
      item.error = '';
    }

    if (!isPublicAuction && item.lotNumberPrefix && !item.lotNumberYear) {
      if (!asset.year) {
        item.lotNumberYear = '09';
      } else if (asset.year < 100 || asset.year > 2009) {
        item.lotNumberYear = String(asset.year % 100).padStart(2, '0');
      } else {
        item.lotNumberYear = '09';
      }
    }

    if (!isPublicAuction && item.lotNumberYear && !item.lotNumberSuffix) {
      const suffixOptions = lotSuffixOptions(
        lotSuffixesAvailable,
        item.lotNumberPrefix,
        item.lotNumberYear,
        (saleAssetData?.saleAssets || []).concat(selected),
      );

      item.lotNumberSuffix = suffixOptions[0];
    }

    setSelected([...selected]);
  }

  function handleSubmit() {
    setFormError('');

    if (selected.length === 0) {
      setFormError('Please select at least one asset.');
      return;
    }

    let hasErrors = false;
    const nextSelected = selected.map(asset => {
      const nextAsset = { ...asset, error: '' };

      if (!asset.lotNumberPrefix) {
        nextAsset.error = 'Please select a prefix.';
        hasErrors = true;
        return nextAsset;
      }

      if (isPublicAuction) {
        const runNumber = parseInt(asset.lotNumberSuffix, 10);

        if (!asset.lotNumberSuffix) {
          nextAsset.error = 'Please enter a run number.';
          hasErrors = true;
        } else if (!runNumber || runNumber <= 0 || String(runNumber) !== String(asset.lotNumberSuffix).trim()) {
          nextAsset.error = 'Run number must be a whole positive integer.';
          hasErrors = true;
        }
      } else {
        if (!asset.lotNumberYear || !asset.lotNumberSuffix) {
          nextAsset.error = 'Please complete the lot number.';
          hasErrors = true;
        }
      }

      return nextAsset;
    });

    setSelected(nextSelected);

    if (hasErrors) {
      setFormError('Unable to add asset(s). Please fix the highlighted field.');
      return;
    }

    const addedAssets = selected.map(asset => ({
      assetId: asset.id,
      lotNumberPrefix: asset.lotNumberPrefix,
      lotNumberYear: isPublicAuction
        ? null
        : (asset.lotNumberYear ? parseInt(asset.lotNumberYear, 10) : null),
      lotNumberSuffix: asset.lotNumberSuffix
        ? parseInt(asset.lotNumberSuffix, 10)
        : null,
      manualRunOrder: isPublicAuction && asset.lotNumberSuffix
        ? parseInt(asset.lotNumberSuffix, 10)
        : null,
    }));

    updateAssets({ variables: { saleId: id, assetInputs: addedAssets } });
  }
};

export default AddableAssets;